import { useEffect, useState } from 'react';
import {
  fetchVisitExternalAnswers,
  type ExternalAnswerMap,
} from '../../../../services/externalAnswers';

/**
 * Values shown by `external-answer` nodes, read from other documents (the
 * episode's care plan). Fetched per visit and never written back.
 *
 * The form component is reused across visit navigation rather than remounted,
 * so the map is reset before every fetch and an in-flight fetch for the
 * previous visit is ignored when it resolves. Either leak would show one
 * patient's thresholds and instructions in another patient's chart.
 */
export function useVisitExternalAnswers(
  visitId: string | undefined
): ExternalAnswerMap {
  const [answers, setAnswers] = useState<ExternalAnswerMap>({});

  useEffect(() => {
    setAnswers({});
    if (!visitId) return;
    let cancelled = false;
    void (async () => {
      try {
        const resolved = await fetchVisitExternalAnswers(visitId);
        if (!cancelled) setAnswers(resolved);
      } catch (error) {
        // Charting continues -- a care plan we could not load must not block the
        // aide. The nodes fall back to their `empty` placeholder.
        if (!cancelled) setAnswers({});
        console.error('Failed to load external answers', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visitId]);

  return answers;
}
